import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchLive, fetchMorePosts, fetchChat, sendChatMessage, sendReaction, fetchReactionsForPosts,
  getLiveActorToken,
  LiveEvent, LivePost, LiveViewersPayload, LiveChatMessage,
  ReactionCounts, ReactionKey, emptyReactionCounts
} from '../services/liveService';
import { useLiveSocket } from '../hooks/useLiveSocket';
import { useLetturaPerPersona } from '../hooks/useCambioPersonaAltraScheda';
import LiveHero from '../components/live/LiveHero';
import LivePostCard from '../components/live/LivePostCard';
import LiveRail, { LiveChatPanel } from '../components/live/LiveRail';
import LiveMobileDock, { MobileTab } from '../components/live/LiveMobileDock';
import { useToast } from '../contexts/ToastContext';
import '../styles/live-event.css';

type FilterKey = 'all' | 'photo' | 'video' | 'link' | 'article';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all',     label: 'Tutto' },
  { key: 'photo',   label: 'Foto' },
  { key: 'video',   label: 'Video' },
  { key: 'link',    label: 'Link' },
  { key: 'article', label: 'Articolo blog' },
];

const leggiTokenAttore = () => getLiveActorToken() || undefined;

const LiveEventPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [live, setLive] = useState<LiveEvent | null>(null);
  const [posts, setPosts] = useState<LivePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);   // guard sincrono anti doppio-fetch
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [viewers, setViewers] = useState<LiveViewersPayload | undefined>(undefined);
  const [mobileTab, setMobileTab] = useState<MobileTab>(null);
  // Chat espansa a tutta larghezza (solo desktop; su mobile c'e' il bottom-sheet)
  const [chatExpanded, setChatExpanded] = useState(false);

  // Chat
  const [chat, setChat] = useState<LiveChatMessage[]>([]);

  // Reactions
  const [reactionsEvent, setReactionsEvent] = useState<ReactionCounts>(emptyReactionCounts());
  const [reactionsByPost, setReactionsByPost] = useState<Record<string, ReactionCounts>>({});
  // myReactionEvent rimosso: hero e\` LinkedIn-style (no toggle). Vedi handleReactEvent.
  const [myReactionsByPost, setMyReactionsByPost] = useState<Record<string, ReactionKey>>({});

  // Fix M11 ciclo 2: floating emoji come QUEUE (array) invece di slot singolo. React batcha
  // gli setState e con uno slot singolo gli emoji intermedi nello stesso tick si perdevano.
  // Con un updater funzionale che concatena, ogni emoji entrato viene preservato.
  const floatSeqRef = useRef(0);
  const [floatQueue, setFloatQueue] = useState<{ emoji: ReactionKey; id: number }[]>([]);

  // Riletto al cambio di persona in un'altra scheda (la pagina non si ricarica più): socket e chat della diretta
  // non restano col token della persona di prima
  const actorToken = useLetturaPerPersona(leggiTokenAttore);
  const isAuthenticated = !!actorToken;

  // ===== Load iniziale =====
  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([
      fetchLive(slug),
      fetchChat(slug).catch(() => [] as LiveChatMessage[])
    ])
      .then(([{ liveEvent, posts, reactions }, chatMessages]) => {
        setLive(liveEvent);
        setPosts(posts);
        setChat(chatMessages);
        if (reactions) {
          setReactionsEvent(reactions.event || emptyReactionCounts());
          setReactionsByPost(reactions.posts || {});
          if (reactions.mine) {
            setMyReactionsByPost(reactions.mine.posts || {});
          }
        }
      })
      .catch((e) => {
        // Diretta riservata (403) e utente NON loggato → manda all'accesso e torna qui dopo l'OTP.
        if (e?.response?.status === 403 && !getLiveActorToken()) {
          navigate(`/accesso-associati?redirect=${encodeURIComponent(`/diretta/${slug}`)}`, { replace: true });
          return;
        }
        setError(e?.response?.data?.error || 'Diretta non disponibile');
      })
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  // ===== Socket handlers =====
  const onCreated = useCallback((p: LivePost) => setPosts(prev => [p, ...prev.filter(x => x._id !== p._id)]), []);
  const onUpdated = useCallback((p: LivePost) => setPosts(prev => prev.map(x => x._id === p._id ? p : x)), []);
  const onDeleted = useCallback((id: string) => setPosts(prev => prev.filter(x => x._id !== id)), []);
  const onStatus  = useCallback((p: { status: string; startedAt?: string | null; accumulatedSeconds?: number }) =>
    setLive(prev => prev ? {
      ...prev,
      status: p.status as any,
      ...(p.startedAt !== undefined ? { startedAt: p.startedAt } : {}),
      ...(p.accumulatedSeconds !== undefined ? { accumulatedSeconds: p.accumulatedSeconds } : {})
    } : prev), []);
  const onViewers = useCallback((payload: LiveViewersPayload) => setViewers(payload), []);
  const onChatMessage = useCallback((msg: LiveChatMessage) => {
    setChat(prev => prev.some(m => m._id === msg._id) ? prev : [...prev, msg]);
  }, []);
  const onChatDeleted = useCallback((messageId: string) => {
    setChat(prev => prev.filter(m => m._id !== messageId));
  }, []);
  const onReactionsEvent = useCallback((counts: ReactionCounts) => setReactionsEvent(counts), []);
  const onReactionsPost = useCallback(({ postId, counts }: { postId: string; counts: ReactionCounts }) => {
    setReactionsByPost(prev => ({ ...prev, [postId]: counts }));
  }, []);
  const onFloatingEmoji = useCallback((emoji: ReactionKey) => {
    // Fix M11: enqueue invece di replace — React batch preserva tutti gli updater funzionali.
    floatSeqRef.current += 1;
    const id = floatSeqRef.current;
    setFloatQueue(prev => [...prev, { emoji, id }]);
  }, []);
  const consumeFloat = useCallback((id: number) => {
    setFloatQueue(prev => prev.filter(f => f.id !== id));
  }, []);

  useLiveSocket(slug, {
    onCreated, onUpdated, onDeleted, onStatus, onViewers,
    onChatMessage, onChatDeleted, onReactionsEvent, onReactionsPost, onFloatingEmoji
  }, actorToken);

  // ===== Actions =====
  const loadOlder = async () => {
    if (!slug || posts.length === 0 || loadingOlderRef.current || !hasMore) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      // In cronologico si caricano i contenuti SUCCESSIVI (after, dal piu recente caricato);
      // altrimenti i PRECEDENTI (before, dal piu vecchio caricato). Entrambi scorrendo in fondo.
      const isChrono = !!live?.chronologicalOrder;
      const older = isChrono
        ? await fetchMorePosts(slug, { after: posts.reduce((max, p) => (p.createdAt > max ? p.createdAt : max), posts[0].createdAt) })
        : await fetchMorePosts(slug, { before: posts.reduce((min, p) => (p.createdAt < min ? p.createdAt : min), posts[0].createdAt) });
      if (older.length === 0) { setHasMore(false); return; }
      const newIds = older.filter(o => !posts.some(p => p._id === o._id)).map(o => o._id);
      setPosts(prev => [...prev, ...older.filter(o => !prev.some(p => p._id === o._id))]);
      // Fix #6 + M12: snapshot reactions counts E my-reactions per i post appena caricati
      if (newIds.length > 0) {
        try {
          const snap = await fetchReactionsForPosts(slug, newIds);
          if (snap.posts) setReactionsByPost(prev => ({ ...prev, ...snap.posts }));
          if (snap.mine?.posts) setMyReactionsByPost(prev => ({ ...prev, ...snap.mine!.posts }));
        } catch (e) { /* best-effort */ }
      }
    } catch (e) {
      /* best-effort: in caso di errore il bottone resta disponibile per riprovare */
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  };

  // Latest-ref pattern: l'IntersectionObserver (creato una volta) chiama sempre
  // la versione piu' recente di loadOlder senza dover essere ricreato a ogni render.
  const loadOlderRef = useRef(loadOlder);
  loadOlderRef.current = loadOlder;

  // Infinite scroll: quando la sentinella in fondo al feed entra in viewport
  // carica i contenuti precedenti. loadOlder fa da solo i guard (hasMore/loading).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadOlderRef.current(); },
      { rootMargin: '300px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loading]);

  // Fix #8: re-throw cosi\` LiveChatPanel.handleSend NON clearia l'input su errore
  const handleSendChat = async (text: string) => {
    if (!slug) return;
    try {
      await sendChatMessage(slug, text);
      // L'evento socket arrivera\` con il messaggio e lo aggiungera\` (con dedup per _id)
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Errore invio messaggio');
      throw e;
    }
  };

  // Hero reactions LinkedIn-style: ogni click ADD +1 (no toggle, no myReaction state).
  // Optimistic incrementa il counter locale; il broadcast socket arrivera\` con i counts veri.
  const handleReactEvent = async (emoji: ReactionKey) => {
    if (!slug || !isAuthenticated) return;
    setReactionsEvent(curr => ({ ...curr, [emoji]: (curr[emoji] || 0) + 1 }));
    try {
      await sendReaction(slug, emoji, null);
    } catch (e: any) {
      // Rollback optimistic
      setReactionsEvent(curr => ({ ...curr, [emoji]: Math.max(0, (curr[emoji] || 0) - 1) }));
      toast.error(e?.response?.data?.error || 'Errore reazione');
    }
  };

  const handleReactPost = async (postId: string, emoji: ReactionKey) => {
    if (!slug || !isAuthenticated) return;
    const prev = myReactionsByPost[postId];
    const wantToggleOff = prev === emoji;
    setMyReactionsByPost(curr => {
      const next = { ...curr };
      if (wantToggleOff) delete next[postId]; else next[postId] = emoji;
      return next;
    });
    try {
      const { action } = await sendReaction(slug, emoji, postId);
      // M10: race → ripristina prev se il server non ha modificato
      if (action === 'noop') {
        setMyReactionsByPost(curr => {
          const next = { ...curr };
          if (prev) next[postId] = prev; else delete next[postId];
          return next;
        });
      }
    } catch (e: any) {
      setMyReactionsByPost(curr => {
        const next = { ...curr };
        if (prev) next[postId] = prev; else delete next[postId];
        return next;
      });
      toast.error(e?.response?.data?.error || 'Errore reazione');
    }
  };

  // Ordine impostato dall'admin: cronologico crescente (dal primo) o piu recenti prima.
  // Effettivo SOLO a diretta non in onda: durante il live si seguono gli ultimi contenuti
  // (newest-first) e si evita che i post realtime avvelenino il cursore di paginazione.
  const chronological = !!live?.chronologicalOrder && live?.status !== 'live';

  const ordered = useMemo(
    () => [...posts].sort((a, b) =>
      (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) ||
      (chronological
        ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    ),
    [posts, chronological]
  );

  const filtered = useMemo(
    () => filter === 'all' ? ordered : ordered.filter(p => p.kind === filter),
    [ordered, filter]
  );

  if (loading) {
    return (
      <div className="live-app">
        <main className="shell" style={{ textAlign: 'center', color: 'var(--un-ink-500)' }}>Caricamento…</main>
      </div>
    );
  }
  if (error || !live) {
    return (
      <div className="live-app">
        <main className="shell" style={{ textAlign: 'center', color: 'var(--un-ink-500)' }}>
          {error || 'Diretta non trovata'}
        </main>
      </div>
    );
  }

  const loadMoreBtn = posts.length > 0 && hasMore ? (
    <div className="load-more">
      <button type="button" onClick={loadOlder} disabled={loadingOlder}>
        {loadingOlder
          ? 'Caricamento…'
          : (chronological ? 'Carica contenuti successivi' : 'Carica contenuti precedenti')}
      </button>
    </div>
  ) : null;

  return (
    <div className="live-app">
      <main className="shell">
        <LiveHero
          live={live}
          viewers={viewers}
          reactionsCounts={reactionsEvent}
          isAuthenticated={isAuthenticated}
          onReact={handleReactEvent}
          floatQueue={floatQueue}
          onFloatConsumed={consumeFloat}
        />

        {chatExpanded && (
          <div className="chat-expanded-wrap">
            <LiveChatPanel
              messages={chat}
              isAuthenticated={isAuthenticated}
              onSend={handleSendChat}
              expanded
              onToggleExpand={() => setChatExpanded(false)}
            />
          </div>
        )}

        <div className="body-grid">
          <div>
            <div className="feed-head">
              <h2>Timeline diretta · {filtered.length} aggiornamenti</h2>
              <div className="filters">
                {FILTERS.map(f => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={filter === f.key ? 'active' : ''}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="feed">
              {filtered.length === 0 ? (
                <div className="post-card" style={{ padding: '32px 18px', textAlign: 'center', color: 'var(--un-ink-500)', fontSize: 14 }}>
                  Nessun contenuto in questa categoria.
                </div>
              ) : (
                filtered.map((p, i) => (
                  <LivePostCard
                    key={p._id}
                    post={p}
                    index={i}
                    reactions={reactionsByPost[p._id]}
                    myReaction={myReactionsByPost[p._id] || null}
                    isAuthenticated={isAuthenticated}
                    onReact={handleReactPost}
                  />
                ))
              )}
            </div>

            {/* Auto-load + bottone di fallback, sempre in fondo al feed. In cronologico
                carica i contenuti successivi (after), altrimenti i precedenti (before). */}
            <div ref={sentinelRef} aria-hidden="true" />
            {loadMoreBtn}
          </div>

          <LiveRail
            viewers={viewers}
            chat={chat}
            isAuthenticated={isAuthenticated}
            onSendChat={handleSendChat}
            reactionsCounts={reactionsEvent}
            chatExpanded={chatExpanded}
            onToggleChatExpand={() => setChatExpanded(true)}
          />
        </div>

        <LiveMobileDock
          active={mobileTab}
          onOpen={(t) => setMobileTab(t)}
          onClose={() => setMobileTab(null)}
          onTab={(t) => setMobileTab(t)}
          viewers={viewers}
          chat={chat}
          isAuthenticated={isAuthenticated}
          onSendChat={handleSendChat}
          reactionsCounts={reactionsEvent}
        />
      </main>
    </div>
  );
};

export default LiveEventPage;
